import { useCallback } from 'react';

import { ButtonGroup, ButtonIcon, MenuItem } from 'shared';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';

import { ChatMessageMoreActionsMenu } from 'features/chat/components/MoreActionsMenu';
import { UUID } from 'features/chat/config/types/Message';
import { useChatInterlocutorContext } from 'features/chat/context/chatInterlocutor';
import { useChatMessagesContext } from 'features/chat/context/chatMessages';
import { useChatMessageDeleteConfirmation } from 'features/chat/hooks/useChatMessageDeleteConfirmation';

type ActionsProps = {
  isIncoming: boolean;
  messageId: UUID;
  text: string;
  fromUserId: string;
  replyTo: UUID | undefined;
  attachments: Attachment[];
};

export const ChatMessageActions = ({
  isIncoming,
  messageId,
  text,
  fromUserId,
  attachments,
  replyTo,
}: ActionsProps) => {
  const { setEditedMessage, setParentMessage, deleteMessage } =
    useChatMessagesContext();

  const { name: interlocutorName } = useChatInterlocutorContext();

  const handleMessageDelete = useChatMessageDeleteConfirmation({
    interlocutorName,
    onConfirm: () => {
      deleteMessage({ messageId });
    },
  });

  const handleEditClick = useCallback(() => {
    setEditedMessage({
      messageId,
      fromUserId,
      text,
      attachments,
      replyTo,
      deleted: false,
      linkPreview: null,
    });
  }, [messageId, fromUserId, text, attachments, replyTo, setEditedMessage]);

  return (
    <ButtonGroup
      flexDirection={!isIncoming ? 'row-reverse' : 'row'}
      position="sticky"
      top={0}
      bottom={0}
    >
      {isIncoming ? (
        <ButtonIcon
          aria-label="reply to message"
          bg="none"
          _hover={{
            bg: 'none',
          }}
          data-testid={`chat-reply-${messageId}`}
          onClick={() =>
            setParentMessage({
              messageId,
              fromUserId,
              text,
              attachments,
              deleted: false,
              linkPreview: null,
            })
          }
        >
          <Icon
            name={iconNames.reply}
            height="16px"
            width="16px"
            fill="var(--fgSecondary)"
          />
        </ButtonIcon>
      ) : null}
      {!isIncoming ? (
        <ChatMessageMoreActionsMenu>
          <MenuItem onClick={handleEditClick}>Edit</MenuItem>
          <MenuItem onClick={handleMessageDelete} color="red">
            Delete
          </MenuItem>
        </ChatMessageMoreActionsMenu>
      ) : null}
    </ButtonGroup>
  );
};
